import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { api, ApiError, type ProductDto, type ProductQuery, type VariantDto } from '../lib/api-client';
import { useCartStore, type CartLine } from '../stores/cart-store';

/**
 * Server state.
 *
 * Every read of API data goes through these hooks. Query keys are declared once
 * in `queryKeys` so an invalidation can never miss a cache entry through a
 * typo'd key.
 */

export const queryKeys = {
  products: (query: ProductQuery) => ['products', query] as const,
  product: (identifier: string) => ['product', identifier] as const,
  paymentStatus: (paymentId: string) => ['payment', paymentId] as const,
  tracking: (orderNumber: string, email: string) => ['tracking', orderNumber, email] as const,
  order: (orderNumber: string) => ['order', orderNumber] as const,
  myOrders: () => ['account', 'orders'] as const,
};

export function useProducts(query: ProductQuery = {}) {
  return useQuery({
    queryKey: queryKeys.products(query),
    queryFn: ({ signal }) => api.products.list(query, signal),
    // A catalogue does not change between two clicks. Five minutes of freshness
    // makes back-navigation instant instead of re-fetching every grid.
    staleTime: 5 * 60 * 1000,
  });
}

export function useProduct(identifier: string | undefined) {
  return useQuery({
    queryKey: queryKeys.product(identifier ?? ''),
    queryFn: ({ signal }) => api.products.get(identifier!, signal),
    enabled: Boolean(identifier),
    staleTime: 5 * 60 * 1000,
  });
}

export interface ResolvedCartLine {
  line: CartLine;
  product: ProductDto | null;
  variant: VariantDto | null;
  /** True when the variant no longer exists - the line is unfulfillable. */
  unavailable: boolean;
  lineTotalPaise: number;
}

/**
 * Join the persisted bag against live product data.
 *
 * The cart stores identifiers; prices and titles are read here, fresh from the
 * server. That is what stops a stale price sitting in someone's bag for weeks -
 * and it means a withdrawn variant surfaces as `unavailable` rather than
 * silently checking out at a price that no longer exists.
 */
export function useCartContents(): {
  lines: ResolvedCartLine[];
  subtotalPaise: number;
  isLoading: boolean;
  isError: boolean;
  hasUnavailable: boolean;
} {
  const cartLines = useCartStore((state) => state.lines);

  const results = useQueries({
    queries: cartLines.map((line) => ({
      queryKey: queryKeys.product(line.productSlug),
      queryFn: ({ signal }: { signal: AbortSignal }) => api.products.get(line.productSlug, signal),
      staleTime: 60 * 1000,
    })),
  }) as UseQueryResult<{ data: ProductDto }, ApiError>[];

  const lines: ResolvedCartLine[] = cartLines.map((line, index) => {
    const result = results[index];
    const product = result?.data?.data ?? null;
    const variant = product?.variants.find((candidate) => candidate.id === line.variantId) ?? null;

    return {
      line,
      product,
      variant,
      unavailable: Boolean(product) && (!variant || variant.status !== 'active'),
      lineTotalPaise: variant ? variant.price_paise * line.quantity : 0,
    };
  });

  return {
    lines,
    subtotalPaise: lines.reduce((total, entry) => total + entry.lineTotalPaise, 0),
    isLoading: results.some((result) => result?.isLoading),
    isError: results.some((result) => result?.isError),
    hasUnavailable: lines.some((entry) => entry.unavailable),
  };
}

/**
 * Poll a payment until the server says it is settled.
 *
 * The browser is never told a payment succeeded by the gateway redirect - it
 * asks *our* server, which only reports success after verifying
 * server-to-server. Polling stops once the payment reaches a terminal state.
 */
export function usePaymentStatus(paymentId: string | undefined, options: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.paymentStatus(paymentId ?? ''),
    queryFn: ({ signal }) => api.checkout.paymentStatus(paymentId!, signal),
    enabled: Boolean(paymentId),
    refetchInterval: (query) => {
      if (options.poll === false) return false;
      const status = query.state.data?.data.status;
      if (!status) return 3000;
      const settled = ['CAPTURED', 'FAILED', 'CANCELLED', 'REFUNDED'].includes(status);
      return settled ? false : 3000;
    },
    // Waiting on a payment is exactly when a stale answer is most harmful.
    staleTime: 0,
  });
}

export function useOrderTracking(orderNumber: string | undefined, email: string | undefined) {
  return useQuery({
    queryKey: queryKeys.tracking(orderNumber ?? '', email ?? ''),
    queryFn: ({ signal }) => api.orders.track(orderNumber!, email!, signal),
    enabled: Boolean(orderNumber && email),
    retry: false,
  });
}

export function useMyOrders(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.myOrders(),
    queryFn: ({ signal }) => api.orders.mine(signal),
    enabled,
    retry: false,
  });
}
